<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">

    <!-- Capa invisible para cerrar los dropdowns al hacer clic fuera -->
    <div v-if="activeDropdown" @click="activeDropdown = null" class="fixed inset-0 z-40"></div>

    <div class="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh] relative z-50">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white">
            {{ form.id ? 'Editar Sucursal' : 'Nueva Sucursal' }}
          </h2>
          <p class="text-xs text-slate-500">Configura la información y mapeo contable de la sucursal.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500 relative z-50">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div class="p-6 overflow-y-auto custom-scrollbar">
        <form @submit.prevent="handleSubmit" id="branchForm" class="space-y-8">

          <!-- SECCIÓN: INFORMACIÓN GENERAL -->
          <div>
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                <Building class="w-4 h-4 text-brand-600" /> Información General
              </h3>
              <label class="flex items-center gap-2 cursor-pointer select-none">
                <span class="text-xs font-medium text-slate-700 dark:text-slate-300">
                  {{ form.is_active ? 'Operativa' : 'Inactiva' }}
                </span>
                <div class="relative">
                  <input type="checkbox" v-model="form.is_active" class="sr-only peer">
                  <div class="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                </div>
              </label>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-5 relative z-10">
              <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Código Interno</label>
                <div class="relative">
                  <Hash class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input v-model="form.code" type="text" disabled class="form-input-premium pl-10 uppercase bg-slate-50" placeholder="Autogenerado" />
                </div>
              </div>

              <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Nombre Sucursal</label>
                <div class="relative">
                  <Building class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input v-model="form.name" type="text" required class="form-input-premium pl-10" placeholder="Ej: Plaza España" />
                </div>
              </div>

              <div class="md:col-span-1">
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Tipo de Operación</label>
                <select v-model="form.type" class="form-input-premium">
                  <option value="STORE">🏪 Sucursal</option>
                  <option value="HQ">🏢 Oficina Central (HQ)</option>
                </select>
              </div>

              <div class="md:col-span-1">
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Gerente Responsable</label>
                <div class="relative">
                  <User class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <select v-model="form.manager" class="form-input-premium pl-10">
                    <option value="">-- Sin asignar --</option>
                    <option v-for="user in managersList" :key="user.id" :value="user.id">{{ user.full_name }}</option>
                  </select>
                </div>
              </div>

              <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Teléfono</label>
                <div class="relative">
                  <Phone class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input v-model="form.phone" type="tel" class="form-input-premium pl-10" />
                </div>
              </div>

              <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Correo de Alertas</label>
                <div class="relative">
                  <Mail class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input v-model="form.email" type="email" class="form-input-premium pl-10" placeholder="sucursal@empresa.com" />
                </div>
              </div>
            </div>
          </div>

          <div class="border-t border-slate-200 dark:border-slate-800"></div>

          <!-- SECCIÓN: MAPEO CONTABLE -->
          <div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2 mb-4">
              <Library class="w-4 h-4 text-brand-600" /> Configuración Contable
            </h3>

            <div class="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl space-y-6 border border-slate-100 dark:border-slate-800">

              <!-- BLOQUES GENERADOS DINÁMICAMENTE CON v-for -->
              <div v-for="(section, idx) in accountSections" :key="idx" class="relative z-20">
                <h4 :class="['text-xs font-bold mb-3 border-b pb-1', section.colorClass]">
                  {{ section.title }}
                </h4>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div v-for="field in section.fields" :key="field.key" class="relative">
                    <label class="text-[11px] font-semibold text-slate-500 uppercase block mb-1">
                      {{ field.label }}
                    </label>

                    <div class="relative">
                      <input
                        type="text"
                        v-model="displayValues[field.key]"
                        @focus="activeDropdown = field.key"
                        @input="onSearchInput(field.key)"
                        :placeholder="'Buscar...'"
                        class="form-input-premium w-full pr-8 text-sm cursor-text"
                        :class="{'border-brand-500 ring-1 ring-brand-500': activeDropdown === field.key}"
                      />
                      <ChevronDown class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>

                    <!-- DROPDOWN LIST -->
                    <div
                      v-if="activeDropdown === field.key"
                      class="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto"
                    >
                      <div
                        v-for="acc in filteredAccounts(field.list, displayValues[field.key])"
                        :key="acc.id"
                        @click="selectAccount(field.key, acc)"
                        class="px-3 py-2 text-sm hover:bg-brand-50 cursor-pointer border-b border-slate-50 last:border-0"
                      >
                        <span class="font-mono text-slate-500 mr-2">{{ acc.code }}</span>
                        <span class="text-slate-700 font-medium">{{ acc.name }}</span>
                      </div>
                      <div v-if="filteredAccounts(field.list, displayValues[field.key]).length === 0" class="p-3 text-xs text-slate-500 text-center">
                        No se encontraron cuentas
                      </div>
                    </div>

                  </div>
                </div>
              </div>

            </div>
          </div>

        </form>
      </div>

      <div class="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 relative z-50">
        <button
          @click="$emit('close')"
          type="button"
          class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="branchForm"
          :disabled="isSaving"
          class="px-6 py-2 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/30 transition-all flex items-center gap-2 disabled:opacity-70"
        >
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          <Save v-else class="w-4 h-4" />
          {{ form.id ? 'Guardar Cambios' : 'Registrar Sucursal' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { X, Save, Loader2, Building, MapPin, Phone, Hash, User, Mail, Library, ChevronDown } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';
import { useSerials } from '@/composables/useSerials';

const { generateCode } = useSerials();
const props = defineProps({ show: Boolean, branchToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError, showWarning } = useToast();

const managersList = ref([]);
const isSaving = ref(false);
const allAccounts = ref([]);

// Estado del Buscador de Cuentas
const activeDropdown = ref(null);
const displayValues = reactive({}); // Guarda el texto que se ve en el input ("110101 - Caja")

// Listas base filtradas para acelerar la búsqueda
const lists = reactive({
  cash: [], loans: [], interests: [], revenues: [], expenses: []
});

const currentUser = SecureStorage.getItem('llevo_user');
const currentCompanyId = currentUser?.company;

const form = reactive({
  id: null, company: currentCompanyId, code: '', name: '', type: 'STORE',
  manager: '', phone: '', email: '', address: '', is_active: true,
  // Cuentas mapeadas
  acc_general_cash: null, acc_cash: null, acc_loan_short_term: null, acc_loan_long_term: null,
  acc_loan_past_due: null, acc_loan_legal: null, acc_loan_provision: null,
  acc_interest_short_term: null, acc_interest_long_term: null,
  acc_revenue_ordinary: null, acc_revenue_past_due: null, acc_revenue_commission: null,
  acc_expense_deterioration: null, acc_expense_write_off: null
});

// Configuración visual de las secciones contables
const accountSections = [
  {
    title: 'Cuentas de Activo (1)',
    colorClass: 'text-indigo-700',
    fields: [
      { key: 'acc_general_cash', label: 'Caja General', list: 'cash' },
      { key: 'acc_cash', label: 'Caja Chica', list: 'cash' },
      { key: 'acc_loan_short_term', label: 'Préstamos HASTA 1 año', list: 'loans' },
      { key: 'acc_loan_long_term', label: 'Préstamos MÁS de 1 año', list: 'loans' },
      { key: 'acc_loan_past_due', label: 'Préstamos Vencidos', list: 'loans' },
      { key: 'acc_loan_legal', label: 'Cobro Judicial', list: 'loans' },
      { key: 'acc_loan_provision', label: 'Estimación Incobrabilidad', list: 'loans' },
      { key: 'acc_interest_short_term', label: 'Intereses por cobrar (C.P.)', list: 'interests' },
      { key: 'acc_interest_long_term', label: 'Intereses por cobrar (L.P.)', list: 'interests' },
    ]
  },
  {
    title: 'Cuentas de Ingresos (4)',
    colorClass: 'text-emerald-700',
    fields: [
      { key: 'acc_revenue_ordinary', label: 'Intereses Ordinarios', list: 'revenues' },
      { key: 'acc_revenue_past_due', label: 'Intereses Moratorios', list: 'revenues' },
      { key: 'acc_revenue_commission', label: 'Comisiones', list: 'revenues' },
    ]
  },
  {
    title: 'Cuentas de Gastos (5)',
    colorClass: 'text-rose-700',
    fields: [
      { key: 'acc_expense_deterioration', label: 'Deterioro de Cartera', list: 'expenses' },
      { key: 'acc_expense_write_off', label: 'Castigo o Saneamiento', list: 'expenses' },
    ]
  }
];

// Mapea los IDs a los Nombres cuando se carga el formulario para Editar
const updateDisplayValues = () => {
  if (!allAccounts.value.length) return;
  accountSections.forEach(sec => sec.fields.forEach(f => {
    if (form[f.key]) {
      const acc = allAccounts.value.find(a => a.id === form[f.key]);
      displayValues[f.key] = acc ? `${acc.code} - ${acc.name}` : '';
    } else {
      displayValues[f.key] = '';
    }
  }));
};

watch(() => props.branchToEdit, (newBranch) => {
  if (newBranch) {
    Object.assign(form, newBranch);
    form.is_active = Boolean(newBranch.is_active);
  } else {
    Object.keys(form).forEach(k => form[k] = null);
    Object.assign(form, { company: currentCompanyId, type: 'STORE', is_active: true });
  }
  updateDisplayValues();
}, { immediate: true });

onMounted(async () => {
  if (!currentCompanyId) return;
  try {
    const resUsers = await apiClient.get(`/users?company=${currentCompanyId}`);
    managersList.value = Array.isArray(resUsers.data) ? resUsers.data : (resUsers.data.data || []);

    const resAcc = await apiClient.get(`/acc_accounts?company_id=${currentCompanyId}`);
    let accounts = resAcc.data.data || resAcc.data;
    allAccounts.value = accounts.filter(a => a.is_selectable);

    // Pre-clasificamos para rendimiento
    lists.cash = allAccounts.value.filter(a => a.code.startsWith('1101'));
    lists.loans = allAccounts.value.filter(a => a.code.startsWith('1104'));
    lists.interests = allAccounts.value.filter(a => a.code.startsWith('1105'));
    lists.revenues = allAccounts.value.filter(a => a.code.startsWith('4101'));
    lists.expenses = allAccounts.value.filter(a => a.code.startsWith('5103'));

    updateDisplayValues();
  } catch (error) {
    console.error("Error cargando dependencias:", error);
  }
});

// Lógica del Buscador
const onSearchInput = (fieldKey) => {
  form[fieldKey] = null; // Si borra o tipea, quitamos el ID seleccionado hasta que elija uno
  activeDropdown.value = fieldKey;
};

const selectAccount = (fieldKey, acc) => {
  form[fieldKey] = acc.id;
  displayValues[fieldKey] = `${acc.code} - ${acc.name}`;
  activeDropdown.value = null; // Cerramos el menú
};

const filteredAccounts = (listKey, query) => {
  const baseList = lists[listKey] || [];
  if (!query) return baseList;

  // Si el query es exactamente lo seleccionado (porque no ha tipeado nuevo), mostrar todo
  const currentId = form[accountSections.flatMap(s=>s.fields).find(f=>f.list===listKey && displayValues[f.key]===query)?.key];
  if(currentId) return baseList;

  const q = query.toLowerCase();
  return baseList.filter(acc => acc.code.toLowerCase().includes(q) || acc.name.toLowerCase().includes(q));
};

const handleSubmit = async () => {
  if (!form.name) return showWarning('El Nombre es obligatorio.');

  isSaving.value = true;
  try {
    const payload = { ...form };
    if (!payload.manager) payload.manager = null;

    if (form.id) {
      await apiClient.put(`/branches/${form.id}`, payload);
      showSuccess('Sucursal modificada correctamente.');
    } else {
      payload.code = await generateCode('BRANCH');
      await apiClient.post('/branches', payload);
      showSuccess('Sucursal registrada correctamente.');
    }
    emit('saved');
    emit('close');
  } catch (error) {
    showError(error.response?.data?.error || 'No se pudo guardar la sucursal.');
  } finally {
    isSaving.value = false;
  }
};
</script>
